'use client'

import RegistrationBirthdayDatePicker from '@app/registration/_components/basic/RegistrationBirthdayDatePicker'
import RegistrationGenderPicker from '@app/registration/_components/basic/RegistrationGenderPicker'
import { FormikCitySelector } from '@components/CitySelector/FormikCitySelector'
import { INLink } from '@components/INLink'
import StepHeader from '@components/Registration/StepHeader'
import {
    Box,
    Checkbox,
    ChevronLeftIcon,
    FormControl,
    FormControlLabel,
    FormHelperText,
    GenderPictogram,
    IconButton,
    styled,
    tss,
    Typography,
} from '@internations/ds'
import { privacyPolicy, registration, termsAndConditions } from '@routes/catalog'
import { useINAppRouter } from '@routes/utils/useINAppRouter'
import { Field, FieldInputProps, useField, useFormikContext } from '@util/Formik'
import bornInLabel from '@util/labels/bornIn'
import livingInLabel from '@util/labels/livingIn'
import privacyPolicyLabel from '@util/labels/privacyPolicy'
import tellUsMoreAboutYouLabel from '@util/labels/tellUsMoreAboutYou'
import termsAndConditionsLabel from '@util/labels/termsAndConditions'
import { BasicRegistrationFormErrors } from '@util/registration/BasicFormModel'
import { BaseSyntheticEvent, ReactElement, useEffect } from 'react'

const useStyles = tss.create(({ theme }) => ({
    genderPickerWrapper: {
        marginTop: theme.spacing(7),
        marginBottom: theme.spacing(2),
    },
    locationFieldWrapper: {
        [theme.breakpoints.up('sm')]: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: theme.spacing(1),
        },
    },
    locationField: {
        // Maintains the same spacing between fields regardless of whether or not `helperText` is visible
        minHeight: 82,

        [`&:last-of-type`]: {
            minHeight: 90,
        },

        [theme.breakpoints.up('sm')]: {
            [`&, &:last-of-type`]: {
                minHeight: 88,
            },
        },
    },
    termsAndConditionsWrapper: {
        marginTop: theme.spacing(2),
    },
}))

const PrivacyPolicyLink = () => (
    <INLink route={privacyPolicy} target="_blank" rel="noreferrer" variant="caption">
        {privacyPolicyLabel}
    </INLink>
)

const TermsAndConditionsLink = () => (
    <INLink route={termsAndConditions} target="_blank" rel="noreferrer" variant="caption">
        {termsAndConditionsLabel}
    </INLink>
)

export const IAgreeToTermsAndConditions = (): ReactElement => (
    <Typography variant="caption">
        {`I accept the InterNations `}
        <PrivacyPolicyLink />
        {` (including the use of cookies) and the `}
        <TermsAndConditionsLink />
        {`. The legal basis for the processing of your data is Art. 6 (1) (b). GDPR`}
    </Typography>
)

const TermsAndConditionsCheckbox = (props: FieldInputProps<'terms'>) => {
    return (
        <FormControlLabel
            control={<Checkbox {...props} data-testid="termsAndConditionsCheckbox" />}
            label={<IAgreeToTermsAndConditions />}
        />
    )
}

const TermsAndConditions = (): ReactElement => {
    const fieldName = 'terms'
    const [{ checked }, { touched, error }, { setValue }] = useField<boolean>({
        name: fieldName,
        type: 'checkbox',
    })
    const hasError = Boolean(touched && error)
    const onChange = (e: BaseSyntheticEvent) => {
        setValue(e.target.checked, true)
    }
    const { classes } = useStyles()

    return (
        <FormControl error={hasError} className={classes.termsAndConditionsWrapper}>
            <Field
                type="checkbox"
                name={fieldName}
                as={TermsAndConditionsCheckbox}
                error={hasError ? error : null}
                helpertext={error ?? null}
                checked={checked}
                onChange={onChange}
            />
            {hasError ? <FormHelperText>{error}</FormHelperText> : null}
        </FormControl>
    )
}

const BackButtonContainer = styled('div')(({ theme }) => ({
    position: 'absolute',
    marginTop: theme.spacing(1),
}))

type SecondScreenProps = {
    backendErrors?: BasicRegistrationFormErrors
}

function SecondScreen({ backendErrors }: SecondScreenProps): ReactElement {
    const { go } = useINAppRouter()
    const formikProps = useFormikContext()
    const { setErrors, setFieldTouched, setFieldValue } = formikProps
    const { classes } = useStyles()
    const handleBackClick = () => {
        go(
            registration.params({
                ...Object.fromEntries(new URLSearchParams(window.location.search).entries()),
                step: 'first',
            })
        )
    }

    useEffect(() => {
        setFieldTouched('gender', false)
        setFieldTouched('bornIn', false)
        setFieldTouched('livingIn', false)
        setFieldTouched('birthday', false)
        setFieldTouched('terms', false)
    }, [setFieldTouched])

    useEffect(() => {
        if (backendErrors) {
            setErrors(backendErrors)
        }
    }, [backendErrors, setErrors])

    return (
        <>
            <BackButtonContainer>
                <IconButton
                    onClick={handleBackClick}
                    Icon={ChevronLeftIcon}
                    aria-label="back"
                    data-testid="backButton"
                />
            </BackButtonContainer>
            <StepHeader Pictogram={GenderPictogram} heading={tellUsMoreAboutYouLabel} />
            <Box className={classes.genderPickerWrapper}>
                <RegistrationGenderPicker />
            </Box>
            <Box className={classes.locationFieldWrapper}>
                <FormikCitySelector
                    fieldName="bornIn"
                    label={bornInLabel}
                    forcedError={backendErrors?.bornIn}
                    onSelect={(place) => setFieldValue('bornIn', place)}
                    className={classes.locationField}
                    fullWidth
                />
                <FormikCitySelector
                    fieldName="livingIn"
                    label={livingInLabel}
                    forcedError={backendErrors?.livingIn}
                    onSelect={(place) => setFieldValue('livingIn', place)}
                    className={classes.locationField}
                    fullWidth
                />
            </Box>
            <RegistrationBirthdayDatePicker />
            <TermsAndConditions />
        </>
    )
}

export default SecondScreen
